import { getRoomTypeLabel, resolveGrmsDisplayState } from '@bms/common';
import type { AccessCard, CardType, EffectiveCardStatus, GrmsStay, Room, Visit } from '@bms/common';
import type { RoomDialogTabHint } from '@/components/desk/attention';

/**
 * The desk palette's results (audit P5), built from what the desk already
 * reads: the rooms list, today's stays, a holder search on the cards route and
 * the visits around today. Pure, so each source is testable without a palette.
 */

export type DeskGroupKey = 'tapped' | 'stays' | 'rooms' | 'cards' | 'visits';
export type DeskIcon = 'stay' | 'room' | 'card' | 'visit';

/** Every palette action is a route: the room dialog, the check-in, the card, the visit. */
export interface DeskAction {
  labelKey: string;
  to: string;
}

export interface DeskResult {
  id: string;
  group: DeskGroupKey;
  icon: DeskIcon;
  title: string;
  /** After the title on the first line, muted. */
  detail?: string;
  /** The second line. */
  caption?: string;
  actions: DeskAction[];
}

export interface DeskGroup {
  key: DeskGroupKey;
  items: DeskResult[];
}

/** The words and clocks the rows are written with, in the desk's language. */
export interface DeskFormat {
  t: (key: string, options?: Record<string, unknown>) => string;
  /** "today 11:00", "tomorrow 08:00", "Wed 16 Oct 11:00". */
  when: (iso: string) => string;
  /** "today", "tomorrow", "Wed 16 Oct". */
  day: (iso: string) => string;
  /** "11:00". */
  time: (iso: string) => string;
}

/** Rows per group: the palette is for finding one thing, not for browsing. */
export const GROUP_LIMIT = 5;

/** Where the groups appear, top to bottom (P5). */
export const GROUP_ORDER: readonly DeskGroupKey[] = ['tapped', 'stays', 'rooms', 'cards', 'visits'];

export const GROUP_LABEL_KEYS: Record<DeskGroupKey, string> = {
  tapped: 'search.group.tapped',
  stays: 'search.group.stays',
  rooms: 'search.group.rooms',
  cards: 'search.group.cards',
  visits: 'search.group.visits',
};

const CARD_TYPE_KEYS: Record<CardType, string> = {
  guest: 'search.cardType.guest',
  staff: 'search.cardType.staff',
  service: 'search.cardType.service',
  master: 'search.cardType.master',
  temporary: 'search.cardType.temporary',
};

/** Card status as a predicate word ("is revoked" / "je opozvana"), never the raw enum. */
export const CARD_STATUS_KEYS: Record<EffectiveCardStatus, string> = {
  pending: 'search.cardStatus.pending',
  active: 'search.cardStatus.active',
  suspended: 'search.cardStatus.suspended',
  revoked: 'search.cardStatus.revoked',
  not_yet_valid: 'search.cardStatus.not_yet_valid',
  expired: 'search.cardStatus.expired',
};

export function cardTypeLabel(t: DeskFormat['t'], type: CardType | string): string {
  const key = CARD_TYPE_KEYS[type as CardType];
  return key ? t(key) : type;
}

export function cardStatusWord(t: DeskFormat['t'], status: EffectiveCardStatus | string): string {
  const key = CARD_STATUS_KEYS[status as EffectiveCardStatus];
  return key ? t(key) : status;
}

/** The room dialog on the Desk, opened on a tab (T4/T5 contract: `?room=&tab=`). */
export function roomPath(roomCode: string, tab?: RoomDialogTabHint): string {
  return `/?room=${encodeURIComponent(roomCode)}${tab ? `&tab=${tab}` : ''}`;
}

/** The guided check-in on the Desk, opened on a ready room (plan-receptionistDesk F1: `?checkin=`). */
export function checkInPath(roomCode: string): string {
  return `/?checkin=${encodeURIComponent(roomCode)}`;
}

/** Case- and accent-blind: "nov" finds "Novák", "kova" finds "Kovač". */
export function foldText(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Every word of the query appears in one of the fields. */
export function matchesQuery(query: string, fields: ReadonlyArray<string | null | undefined>): boolean {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  const haystack = fields.filter(Boolean).map((field) => foldText(field as string));
  return words.every((word) => haystack.some((field) => field.includes(word)));
}

/** In-house stays, once each: a departure or an arrival is also in house. */
export function uniqueStays(stays: { arrivals?: GrmsStay[]; departures?: GrmsStay[]; inHouse?: GrmsStay[] } | undefined): GrmsStay[] {
  const byRoom = new Map<string, GrmsStay>();
  for (const stay of [...(stays?.inHouse ?? []), ...(stays?.departures ?? []), ...(stays?.arrivals ?? [])]) {
    if (!byRoom.has(stay.roomCode)) byRoom.set(stay.roomCode, stay);
  }
  return [...byRoom.values()];
}

function roomTitle(stay: Pick<GrmsStay, 'roomCode' | 'roomName' | 'roomLabel'>): string {
  return stay.roomLabel || stay.roomName || stay.roomCode;
}

export function stayResult(stay: GrmsStay, keyCounts: ReadonlyMap<string, number> | undefined, fmt: DeskFormat): DeskResult {
  const { t } = fmt;
  const checkout = stay.checkoutExpected
    ? t(stay.overdueCheckout ? 'search.stay.overdue' : 'search.stay.due', { time: fmt.when(stay.checkoutExpected) })
    : t('search.stay.noCheckout');
  const keys = keyCounts?.get(stay.roomCode);
  return {
    id: `stay:${stay.roomCode}`,
    group: 'stays',
    icon: 'stay',
    title: t('search.stay.title', { guest: stay.guestName || t('search.stay.unnamedGuest'), room: roomTitle(stay) }),
    caption: [checkout, keys != null ? t('search.stay.keys', { count: keys }) : null].filter(Boolean).join(' · '),
    actions: [
      { labelKey: 'search.action.openStay', to: roomPath(stay.roomCode, 'stay') },
      { labelKey: 'search.action.doorKeys', to: roomPath(stay.roomCode, 'door') },
    ],
  };
}

/**
 * A room row. `canCheckIn` — the room is sellable (`isSellable`) and the desk
 * holds `grms:write` — puts "Check in" first (F1).
 */
export function roomResult(room: Room, fmt: DeskFormat, canCheckIn = false): DeskResult {
  const { t } = fmt;
  const name = room.roomLabel || room.roomName || room.roomCode;
  const status = t(`grms.statusShort.${resolveGrmsDisplayState(room)}`);
  const temperature = room.temperature != null ? room.temperature.toFixed(1) : null;
  const reading = temperature == null
    ? null
    : room.hvacSetpoint != null
      ? t('search.room.reading', { temperature, setpoint: room.hvacSetpoint.toFixed(1) })
      : t('search.room.temperature', { temperature });
  return {
    id: `room:${room.roomCode}`,
    group: 'rooms',
    icon: 'room',
    title: name,
    detail: [name !== room.roomCode ? room.roomCode : null, room.roomType ? getRoomTypeLabel(room.roomType, fmt.t) : null, room.floorName].filter(Boolean).join(' · ') || undefined,
    caption: [status, room.guestName, reading].filter(Boolean).join(' · '),
    actions: [
      ...(canCheckIn ? [{ labelKey: 'search.action.checkIn', to: checkInPath(room.roomCode) }] : []),
      { labelKey: 'search.action.openRoom', to: roomPath(room.roomCode) },
      { labelKey: 'search.action.climate', to: roomPath(room.roomCode, 'climate') },
      { labelKey: 'search.action.doorKeys', to: roomPath(room.roomCode, 'door') },
    ],
  };
}

export function cardResult(card: AccessCard, fmt: DeskFormat, group: DeskGroupKey = 'cards'): DeskResult {
  const { t } = fmt;
  const actions: DeskAction[] = [{ labelKey: 'search.action.openCard', to: `/cards?card=${encodeURIComponent(card.id)}` }];
  // A key opens its room on Door & keys (UX-7, the T5 contract).
  if (card.roomCode) actions.push({ labelKey: 'search.action.doorKeys', to: roomPath(card.roomCode, 'door') });
  return {
    id: `${group}:${card.id}`,
    group,
    icon: 'card',
    title: card.holderName || t('search.card.noHolder'),
    detail: card.cardUid,
    caption: [
      cardTypeLabel(t, card.cardType),
      cardStatusWord(t, card.effectiveStatus),
      card.validUntil ? t('search.card.until', { time: fmt.when(card.validUntil) }) : null,
      card.roomCode ? t('search.card.room', { room: card.roomName || card.roomCode }) : null,
    ].filter(Boolean).join(' · '),
    actions,
  };
}

export function visitResult(visit: Visit, fmt: DeskFormat): DeskResult {
  const { t } = fmt;
  return {
    id: `visit:${visit.id}`,
    group: 'visits',
    icon: 'visit',
    title: visit.visitorName || visit.visitorCompany || visit.visitorId,
    detail: t('search.visit.window', {
      day: fmt.day(visit.scheduledFrom),
      from: fmt.time(visit.scheduledFrom),
      until: fmt.time(visit.scheduledUntil),
    }),
    caption: [
      visit.visitorName ? visit.visitorCompany : null,
      visit.purpose,
      visit.status === 'checked_in' ? t('search.visit.checkedIn') : null,
    ].filter(Boolean).join(' · ') || undefined,
    actions: [{ labelKey: 'search.action.openVisit', to: `/visitors?visit=${encodeURIComponent(visit.id)}` }],
  };
}

const CARD_ORDER: Record<string, number> = { active: 0, pending: 1, not_yet_valid: 2, suspended: 3, expired: 4, revoked: 5 };

export interface DeskSources {
  rooms?: Room[];
  stays?: GrmsStay[];
  keyCounts?: ReadonlyMap<string, number>;
  /** The holder search's answer for this query (the route already filtered it). */
  cards?: AccessCard[];
  visits?: Visit[];
  /** Rooms the desk may check a guest into now: sellable, with `grms:write`. */
  checkInRooms?: ReadonlySet<string>;
}

/**
 * Typed text: every source, in P5's order, at most `GROUP_LIMIT` rows each.
 * A guest's room also answers to the guest's name, so "nov" finds Novak's stay
 * and the room she is in.
 */
export function searchDesk(query: string, sources: DeskSources, fmt: DeskFormat): DeskGroup[] {
  if (!query.trim()) return [];
  const stays = (sources.stays ?? [])
    .filter((stay) => matchesQuery(query, [stay.guestName, stay.roomCode, stay.roomName, stay.roomLabel]))
    .slice(0, GROUP_LIMIT)
    .map((stay) => stayResult(stay, sources.keyCounts, fmt));
  const rooms = (sources.rooms ?? [])
    .filter((room) => matchesQuery(query, [room.roomCode, room.roomName, room.roomLabel, room.roomType, room.guestName]))
    .slice(0, GROUP_LIMIT)
    .map((room) => roomResult(room, fmt, sources.checkInRooms?.has(room.roomCode)));
  const cards = [...(sources.cards ?? [])]
    .sort((a, b) => (CARD_ORDER[a.effectiveStatus] ?? 9) - (CARD_ORDER[b.effectiveStatus] ?? 9))
    .slice(0, GROUP_LIMIT)
    .map((card) => cardResult(card, fmt));
  const visits = (sources.visits ?? [])
    .filter((visit) => visit.status !== 'cancelled' && visit.status !== 'checked_out')
    .filter((visit) => matchesQuery(query, [visit.visitorName, visit.visitorCompany, visit.purpose, visit.hostName]))
    .slice(0, GROUP_LIMIT)
    .map((visit) => visitResult(visit, fmt));
  return compact([
    { key: 'stays', items: stays },
    { key: 'rooms', items: rooms },
    { key: 'cards', items: cards },
    { key: 'visits', items: visits },
  ]);
}

/** A tapped card, preselected, with the stay and the room it opens. */
export function tappedCardGroups(card: AccessCard, sources: DeskSources, fmt: DeskFormat): DeskGroup[] {
  const stay = card.roomCode ? (sources.stays ?? []).find((s) => s.roomCode === card.roomCode) : undefined;
  const room = card.roomCode ? (sources.rooms ?? []).find((r) => r.roomCode === card.roomCode) : undefined;
  return compact([
    { key: 'tapped', items: [cardResult(card, fmt, 'tapped')] },
    { key: 'stays', items: stay ? [stayResult(stay, sources.keyCounts, fmt)] : [] },
    { key: 'rooms', items: room ? [roomResult(room, fmt, sources.checkInRooms?.has(room.roomCode))] : [] },
  ]);
}

function compact(groups: DeskGroup[]): DeskGroup[] {
  return groups.filter((group) => group.items.length > 0);
}
